"use client";

import { Loader2 } from "lucide-react";
import { usePathname } from "next/navigation";
import { useTransition } from "react";
import { toast } from "~/hooks/use-toast";
import { useInfiniteScroll } from "~/hooks/useInfiniteScroll";
import { type SelectVideoWithTranslationsAndUsers } from "~/server/db/videos/schema";
import { type OrderDirEnum } from "~/types/db";
import { type VideoSortColumn } from "~/types/videos";

import { VideoCard } from "./VideoCard";
import VideoListControls from "./VideoListControls";
import { deleteVideoAction } from "./actions";

const ITEMS_PER_PAGE = 20;

export function VideoList({
  videos,
  discover,
  hasMore,
  totalCount,
  orderBy,
  orderDir,
}: {
  videos: SelectVideoWithTranslationsAndUsers[];
  discover?: boolean;
  hasMore?: boolean;
  totalCount?: number;
  orderBy: VideoSortColumn;
  orderDir: OrderDirEnum;
}) {
  const [isPending, startTransition] = useTransition();
  const pathname = usePathname();

  const {
    data: allVideos,
    ref,
    hasMore: stillHasMore,
  } = useInfiniteScroll({
    initialData: videos,
    hasMore: hasMore ?? false,
    paginationType: "offset",
    limit: ITEMS_PER_PAGE,
    onLoadMore: async ({ offset = 0 }) => {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("offset", offset.toString());
      searchParams.set("limit", ITEMS_PER_PAGE.toString());
      searchParams.set("orderBy", orderBy);
      searchParams.set("orderDir", orderDir);

      const res = await fetch(`/api${pathname}?${searchParams.toString()}`);

      const { data, hasMore } = (await res.json()) as {
        data: SelectVideoWithTranslationsAndUsers[];
        hasMore: boolean;
      };
      return { data, hasMore };
    },
  });

  const handleDeleteVideo = (formData: FormData) => {
    startTransition(() => {
      deleteVideoAction(formData).catch((error) => {
        console.error(error);
        toast({
          title: "Acks!",
          description: "Video could not be removed.",
        });
      });
    });
  };

  return (
    <>
      <VideoListControls
        className="mb-4"
        discover={discover}
        totalCount={totalCount}
        orderBy={orderBy}
        orderDir={orderDir}
      />
      <div className="grid grid-cols-1 gap-6 pb-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {allVideos.map((video) => (
          <VideoCard
            key={video.id}
            video={video}
            discover={discover}
            onDelete={handleDeleteVideo}
            isPending={isPending}
          />
        ))}
      </div>
      {stillHasMore && (
        <div
          ref={ref}
          className="col-span-full flex h-4 justify-center pb-24 opacity-50"
        >
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      )}
    </>
  );
}
